// PR-16.2 — Shared player-facing league card. Used by:
//   - Leagues.tsx (mobile "Mina serier" tab) via variant="my_series"
//   - LeagueList.tsx (mobile "Anmäl dig" tab) via variant="register"
//   - LeagueList.tsx (mobile "Arkiv" tab) via variant="archive"
//
// Visual model (per Rickard's designer reference):
//   ┌───────────────────────────────────────────────┐
//   │ {SeriesName}                  [STATUS BADGE]  │
//   │                                                │
//   │ {Lag-Badge: Herr/Dam/Mix}  Mån, Ons, Fre      │
//   │ {Hall name}                                    │
//   │ Sista {speldagen|anmälningsdag}: 30 apr        │
//   │                                                │
//   │ {Optional sponsor logo, click-through}         │
//   └───────────────────────────────────────────────┘
//
// Variant differences (per Q1 + Q5):
//   my_series  — date row uses league.end_date as "Sista speldagen"
//                weekdays derive from active-round matches (fallback play_days)
//                sponsor: sponsor_my_series_*
//   register   — date row uses league.registration_deadline as "Sista anmälningsdag"
//                weekdays from league.play_days (admin's checkboxes)
//                sponsor: sponsor_register_*
//   archive    — same as register but no sponsor + dimmed (closed series)
//
// Whole card is wrapped in a <Link to={`/leagues/${id}`}> for tap-target;
// the optional sponsor link uses event.stopPropagation() so a sponsor
// click navigates to the sponsor URL, not the league detail.

import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronRight, MapPin, CalendarDays, Trophy } from "lucide-react";
import {
  deriveActiveRoundWeekdays,
  weekdaysToShortLabels,
  safeSponsorClickProps,
  formatLeagueDate,
} from "@/lib/leaguePlayerHelpers";

const STATUS_LABELS: Record<string, string> = {
  draft: "Utkast",
  registration_open: "Anmälan",
  active: "Pågår",
  completed: "Avslutad",
  archived: "Arkiv",
};

const STATUS_VARIANT: Record<string, "default" | "secondary" | "outline"> = {
  registration_open: "secondary",
  active: "default",
  completed: "outline",
  archived: "outline",
  draft: "outline",
};

const CATEGORY_LABELS: Record<string, string> = {
  herr: "Herr",
  dam: "Dam",
  mix: "Mix",
};

// Variant-specific column subset. Sponsor + date sources differ but
// most fields are shared. Caller can pass any superset of these.
export interface PlayerLeagueCardData {
  id: string;
  name: string;
  category: string;
  status: string;
  start_date: string | null;
  end_date: string | null;
  registration_deadline: string | null;
  play_days: string[];
  halls?: { name: string } | null;
  sponsor_my_series_logo_url?: string | null;
  sponsor_my_series_url?: string | null;
  sponsor_register_logo_url?: string | null;
  sponsor_register_url?: string | null;
}

export type PlayerLeagueCardVariant = "my_series" | "register" | "archive";

export default function PlayerLeagueCard({
  league,
  variant,
  /** PR-16.2 (Q2): for variant="my_series", caller passes the active
   * round number + the list of league_matches so the card can display
   * the actually-scheduled weekdays. Falls back to league.play_days
   * if either is missing. Other variants ignore these props. */
  activeRoundNumber = null,
  matches = [],
}: {
  league: PlayerLeagueCardData;
  variant: PlayerLeagueCardVariant;
  activeRoundNumber?: number | null;
  matches?: { round: number; scheduled_date: string | null }[];
}) {
  // Weekday source per Q2 spec
  const weekdayKeys =
    variant === "my_series"
      ? (() => {
          const derived = deriveActiveRoundWeekdays(matches, activeRoundNumber);
          return derived.length > 0 ? derived : league.play_days;
        })()
      : league.play_days;
  const weekdays = weekdaysToShortLabels(weekdayKeys);

  // Date row source per Q1 spec
  const dateLabel =
    variant === "my_series"
      ? "Sista speldagen"
      : "Sista anmälningsdag";
  const dateValue =
    variant === "my_series"
      ? formatLeagueDate(league.end_date)
      : formatLeagueDate(league.registration_deadline);

  // Sponsor source per Q5 spec
  const sponsorLogo =
    variant === "my_series"
      ? league.sponsor_my_series_logo_url
      : variant === "register"
        ? league.sponsor_register_logo_url
        : null; // archive: no sponsor
  const sponsorClickUrl =
    variant === "my_series"
      ? league.sponsor_my_series_url
      : variant === "register"
        ? league.sponsor_register_url
        : null;
  const sponsorClickProps = safeSponsorClickProps(sponsorClickUrl);

  return (
    <Link
      to={`/leagues/${league.id}`}
      className={
        variant === "archive" ? "block opacity-70" : "block"
      }
    >
      <Card className="hover:bg-muted/50 transition-colors cursor-pointer">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            {/* Trophy avatar */}
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Trophy className="h-5 w-5 text-primary" />
            </div>

            <div className="flex-1 min-w-0 space-y-2">
              {/* Row 1: name + status badge */}
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-base leading-tight truncate">
                  {league.name}
                </h3>
                <Badge
                  variant={STATUS_VARIANT[league.status] ?? "outline"}
                  className="text-[10px] shrink-0"
                >
                  {STATUS_LABELS[league.status] ?? league.status}
                </Badge>
              </div>

              {/* Row 2: gender badge + weekdays inline */}
              <div className="flex items-center gap-2 flex-wrap text-sm text-muted-foreground">
                <Badge variant="secondary" className="text-xs">
                  {CATEGORY_LABELS[league.category] ?? league.category}
                </Badge>
                {weekdays.length > 0 && (
                  <span className="inline-flex items-center gap-1">
                    <CalendarDays className="h-3.5 w-3.5" />
                    {weekdays.join(", ")}
                  </span>
                )}
              </div>

              {/* Row 3: location */}
              {league.halls?.name && (
                <p className="text-sm text-muted-foreground inline-flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {league.halls.name}
                </p>
              )}

              {/* Row 4: variant-specific date */}
              {dateValue && (
                <p className="text-sm">
                  <span className="text-muted-foreground">{dateLabel}: </span>
                  <span className="font-medium">{dateValue}</span>
                </p>
              )}

              {/* Row 5: optional sponsor logo */}
              {sponsorLogo && (
                <div className="pt-1">
                  {sponsorClickProps ? (
                    <a
                      href={sponsorClickProps.href}
                      target={sponsorClickProps.target}
                      rel={sponsorClickProps.rel}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-block"
                    >
                      <SponsorLogo url={sponsorLogo} />
                    </a>
                  ) : (
                    <SponsorLogo url={sponsorLogo} />
                  )}
                </div>
              )}
            </div>

            <ChevronRight className="h-5 w-5 text-muted-foreground mt-1 shrink-0" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

// Small img wrapper that hides itself on load failure (admin pasted a
// non-image URL or the host blocks hot-linking). Mirrors the
// SponsorThumb pattern from PR-16.1 LeagueForm.
function SponsorLogo({ url }: { url: string }) {
  return (
    <img
      src={url}
      alt="Sponsor"
      className="h-8 w-auto object-contain"
      onError={(e) => {
        (e.currentTarget as HTMLImageElement).style.display = "none";
      }}
    />
  );
}
